import BrowserbaseRenderer from '@/components/BrowserbaseRenderer/BrowserbaseRenderer.js';
import LoadingScreen from '@/components/LoadingScreen';
import ApplicationCancelledView from '@/components/TaskHub/ApplicationCancelledView.js';
import ApplicationCompletedView from '@/components/TaskHub/ApplicationCompletedView.js';
import ApplicationHubSider from '@/components/TaskHub/ApplicationHubSider.js';
import BetaViewSwitch from '@/components/TaskHub/BetaViewSwitch.js';
import PreCheckList from '@/components/TaskHub/PreCheckList';
import TaskRunningView from '@/components/TaskHub/TaskRunningView.js';
import { ResizableHandle } from '@/components/ui/resizable';
import socketEvents from '@/constants/socketEvents.ts';
import { useDocument } from '@/context/DocumentContext';
import { countLength } from '@/helpers/helperFunctions';
import backendApis from '@/services/backendApis';
import { AgentTaskState, BrowserRun, BrowserTask } from '@/types';
import {
  CarrierStateInfo,
  CarrierStatus,
  CarrierVerification,
} from '@/types/carrier.js';
import { FieldJobType } from '@/types/workflowDocs.js';
import {
  RowStatus,
  WorkflowLog,
  WorkflowLogStatus,
  WorkflowRunListObject,
  WorkflowState,
} from '@/types/workflowRuns';
import { useAuth } from '@clerk/clerk-react';
import React, { useEffect, useRef, useState } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';
import { toast } from 'sonner';

const ApplicationHub = () => {
  const { getToken } = useAuth();
  const { setDocumentId } = useDocument();
  const [application, setApplication] = useState<WorkflowRunListObject | null>(
    null,
  );
  const [browserRuns, setBrowserRuns] = useState<BrowserRun[] | null>(null);
  const [currentBrowserTask, setCurrentBrowserTask] =
    useState<BrowserRun | null>(null);
  const [preCheck, setPreCheck] = useState<boolean | null>(null);
  const [isAutomationReady] = useState<boolean>(false);
  const intervalRef = useRef<any>(null);
  const [qnaQuestions, setQnaQuestions] = useState<any>({});
  const [isQuestionSubmitted] =
    useState<boolean>(false);
  const [verification, setVerification] = useState<CarrierVerification | null>(
    null,
  );

  useEffect(() => {
    return () => {
      setDocumentId(null);
    };
  }, []);

  const getTaskById = async (taskId: string) => {
    try {
      const params = {
        workflow_run_id: taskId,
      };
      const response = await backendApis.getWorkflowRunObject(params);
      const item: WorkflowRunListObject = response.data.item;
      return item;
    } catch {
      toast.error('Failed to get tasks');
    }
  };

  const getTaskData = async () => {
    const parts = window.location.href.split('/');
    const taskId = parts.at(-1);
    const task = await getTaskById(taskId);
    setApplication(task);
  };

  const updateTaskDataLog = async () => {
    try {
      const params = {
        workflow_log_id: application?.workflow_log?.workflow_log_id,
        workflow_run_id: application?.workflow_run?.workflow_run_id,
      };
      const res = await backendApis.getWorkflowLog(params);
      if (res.err) {
        console.error('Error updating task data log:', res.err);
        return;
      }
      setApplication((prev) => {
        return {
          ...prev,
          workflow_log: res.data.workflow_log,
        };
      });
    } catch (error) {
      console.error('Error updating task data log:', error);
    }
  };

  useEffect(() => {
    getTaskData();
  }, []);

  useEffect(() => {
    if (application && countLength(browserRuns) === 0 && !intervalRef.current) {
      getAllBrowserTasks();
      intervalRef.current = setInterval(() => {
        getAllBrowserTasks();
      }, 6000);
    }
    if (application) {
      const log = application.workflow_log;
      checkPreCheckStatus(log);
      const interval = setInterval(updateTaskDataLog, 5000);
      return () => clearInterval(interval);
    }
    return () => clearInterval(intervalRef.current);
  }, [application]);

  const checkPreCheckStatus = (log: WorkflowLog) => {
    if (!log) {
      return;
    }
    if (preCheck && isAutomationReady) {
      return;
    }
    const lastProcessingInitiatedIndex = log.workflow_log_records
      .map((record) => record.log_record_status)
      .lastIndexOf(WorkflowLogStatus.PROCESSING_INITIATED);

    const recordsAfterProcessingInitiated =
      lastProcessingInitiatedIndex >= 0
        ? log.workflow_log_records.slice(lastProcessingInitiatedIndex + 1)
        : [];

    const failedLog = recordsAfterProcessingInitiated.find(
      (record) =>
        record.log_record_status === WorkflowLogStatus.PRECHECKLIST_FAILED,
    );
    const succeededLog = recordsAfterProcessingInitiated.find(
      (record) =>
        record.log_record_status === WorkflowLogStatus.PRECHECKLIST_SUCCEEDED,
    );
    const automationReadyLog = recordsAfterProcessingInitiated.find(
      (record) =>
        record.log_record_status ===
          WorkflowLogStatus.BROWSER_AGENT_RUN_INITIATED ||
        record.log_record_status === WorkflowLogStatus.BROWSER_AGENT_INITIATED,
    );

    const preCheckFailed = !!failedLog;
    const isAutomationReadyLog = !!automationReadyLog;

    if (isAutomationReadyLog) {
      setPreCheck(true);
    } else {
      if (succeededLog) {
        setPreCheck(true);
      } else if (preCheckFailed) {
        setPreCheck(false);
      }
    }
  };

  const getAllBrowserTasks = async () => {
    const browserRunIds = application.workflow_run.browser_run_ids;
    const params = {
      browser_run_ids: browserRunIds,
    };
    if (!browserRunIds || browserRunIds.length === 0) {
      return;
    }
    const response = await backendApis.getBrowserRunsBatch(params);
    if (response.err) {
      console.error('Error fetching tasks:', response.err);
      return;
    }
    const responseBrowserTasks = response.data.browser_runs;
    const tasks: BrowserTask[] = [];
    browserRunIds.forEach((taskId) => {
      const task = responseBrowserTasks.find(
        (t: BrowserTask) => t.browser_run_id === taskId,
      );
      if (task) {
        tasks.push(task);
      }
    });
    setBrowserRuns(tasks);
  };

  const getBrowserAgentQuestions = async (browser_run_id) => {
    try {
      const params = {
        browser_run_id,
      };
      const token = await getToken();
      if (!token) {
        setTimeout(() => {
          getBrowserAgentQuestions(browser_run_id);
        }, 1000);
      }
      const gettingQuestions = await backendApis.getBrowserRunQuestions(
        params,
        token,
      );
      if (gettingQuestions.err) {
        console.error(
          'Error fetching browser questions:',
          gettingQuestions.err,
        );
        return;
      }
      if (Object.keys(gettingQuestions.data.questions)?.length > 0) {
        const page_name = Object.keys(gettingQuestions?.data?.questions)?.[0];
        const questionIds =
          gettingQuestions.data.questions[page_name]?.questions
            ?.filter((q) => q.status === 'waiting_for_user_answer')
            ?.map((q) => q.element_id) ?? [];

        if (questionIds.length > 0) {
          const userQuestions = gettingQuestions.data.questions[
            page_name
          ]?.elements?.filter((q) => questionIds.includes(q.id));
          setQnaQuestions({
            data: {
              browser_run_id,
              job_context_id:
                application?.workflow_log?.workflow_log_records?.slice(-1)[0]
                  ?.log_record_payload?.job_staging?.job_context_id,
              page_name,
              workflow_run_id: application?.workflow_run?.workflow_run_id,
            },
            questions: userQuestions,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching browser questions:', error);
    }
  };

  useEffect(() => {
    if (browserRuns && countLength(browserRuns) > 0) {
      const runningTask = browserRuns?.find(
        (a) =>
          a.browser_run_state === AgentTaskState.RUNNING ||
          a.browser_run_state === socketEvents.WAITING_FOR_ONCALL_TO_ANSWER ||
          a.browser_run_state === socketEvents.WAITING_FOR_USER_TO_ANSWER,
      );
      if (runningTask) {
        setCurrentBrowserTask(runningTask);
      }
    }
  }, [browserRuns]);

  useEffect(() => {
    if (currentBrowserTask) {
      const haveQuestions =
        currentBrowserTask.browser_run_state ===
        socketEvents.WAITING_FOR_USER_TO_ANSWER;

      if (
        haveQuestions &&
        Object.keys(qnaQuestions).length === 0 &&
        !isQuestionSubmitted
      ) {
        getBrowserAgentQuestions(currentBrowserTask.browser_run_id);
      }
    }
  }, [currentBrowserTask]);

  const lastBrowserAgentInitiatedLog =
    application?.workflow_log?.workflow_log_records.find(
      (a) =>
        a.log_record_status === WorkflowLogStatus.BROWSER_AGENT_RUN_INITIATED ||
        a.log_record_status === WorkflowLogStatus.BROWSER_AGENT_INITIATED,
    );

  const browserAgentCompletedRecord =
    application?.workflow_log?.workflow_log_records.find(
      (a) => a.log_record_status === WorkflowLogStatus.BROWSER_AGENT_COMPLETED,
    );

  const rowStatus =
    application?.workflow_log?.workflow_log_records.at(-1)?.log_record_status ||
    application?.workflow_context?.workflow_state;

  const siderPanelProps = {
    className: 'min-h-screen grow bg-gray-50 p-4',
    defaultSize: 20,
  };

  const mainPanelProps = {
    className: 'min-h-screen grow bg-kay-slate p-9 md:p-12',
    defaultSize: 80,
  };

  const carrierAccessStarted =
    application?.workflow_log?.workflow_log_records?.find(
      (a) => a.log_record_status === FieldJobType.CARRIER_ACCESS_STARTED,
    );
  const carrierAccessCompleted =
    application?.workflow_log?.workflow_log_records?.find(
      (a) => a.log_record_status === FieldJobType.CARRIER_ACCESS_COMPLETED,
    );

  const updateVerification = async () => {
    try {
      const params = {
        verification_id:
          carrierAccessStarted?.log_record_payload?.job_staging?.job_result
            ?.verification_id,
      };
      const response = await backendApis.getCarrierAccessVerification(params);
      if (response.err) {
        throw new Error(response.err);
      }
      const verification: CarrierVerification =
        response.data.carrier_verification;
      setVerification(verification);
      console.log('Verification:', verification);
    } catch (e) {
      console.error('Error updating verification:', e);
    }
  };

  useEffect(() => {
    if (carrierAccessStarted && !carrierAccessCompleted) {
      updateVerification();
    }
  }, [carrierAccessStarted, carrierAccessCompleted]);

  const workflowCancelled =
    application?.workflow_context?.workflow_state === WorkflowState.CANCELLED ||
    application?.workflow_run_log_statuses?.includes(
      WorkflowLogStatus.WORKFLOW_CANCELLED,
    );

  if (application && workflowCancelled) {
    return (
      <div className="flex min-h-full">
        <PanelGroup className="" direction="horizontal">
          <Panel {...siderPanelProps}>
            <ApplicationHubSider
              application={application}
              browserTasks={browserRuns}
              key={currentBrowserTask?.browser_run_id || 'none'}
            />
          </Panel>

          <ResizableHandle withHandle />

          <Panel {...mainPanelProps}>
            <ApplicationCancelledView application={application} />
          </Panel>
        </PanelGroup>
      </div>
    );
  }

  if (
    browserAgentCompletedRecord ||
    application?.workflow_run?.status === RowStatus.COMPLETED ||
    application?.workflow_run_log_statuses?.includes(
      WorkflowLogStatus.WORKFLOW_COMPLETED,
    ) ||
    application?.workflow_context?.workflow_state === WorkflowState.COMPLETED ||
    rowStatus === RowStatus.WORKFLOW_COMPLETED
  ) {
    return (
      <div className="flex min-h-full">
        <PanelGroup direction="horizontal">
          <Panel {...siderPanelProps}>
            <ApplicationHubSider
              application={application}
              browserTasks={browserRuns}
            />
          </Panel>
          <Panel {...mainPanelProps}>
            <ApplicationCompletedView application={application} />
          </Panel>
        </PanelGroup>
      </div>
    );
  }

  if (!application || !preCheck) {
    return (
      <div className="flex min-h-full">
        <PanelGroup className="" direction="horizontal">
          <Panel {...siderPanelProps}>
            <ApplicationHubSider
              application={application}
              browserTasks={browserRuns}
              disableBackButton
            />
          </Panel>
          <Panel {...mainPanelProps}>
            <div className="flex items-center justify-center">
              {application ? (
                preCheck === null ? (
                  <LoadingScreen />
                ) : (
                  <div>
                    <PreCheckList taskData={application} />
                  </div>
                )
              ) : (
                <LoadingScreen />
              )}
            </div>
          </Panel>
        </PanelGroup>
      </div>
    );
  }

  if (carrierAccessStarted && !carrierAccessCompleted && verification) {
    const states = verification?.carrier_states || {};
    const carrierNames = Object.keys(states);

    const allStates = [];
    for (let i = 0; i < carrierNames.length; i++) {
      const key = carrierNames[i];
      allStates.push({
        name: key,
        ...states[key],
      });
    }

    const pendingStates: CarrierStateInfo[] = allStates.filter(
      (state) =>
        state.carrier_status === CarrierStatus.Pending ||
        state.carrier_status === CarrierStatus.WaitingForUser ||
        state.carrier_status === CarrierStatus.Failed,
    );
    const pendingState = pendingStates[0];

    return (
      <div className="flex min-h-full">
        <PanelGroup direction="horizontal">
          <Panel {...siderPanelProps}>
            <ApplicationHubSider
              application={application}
              browserTasks={browserRuns}
            />
          </Panel>
          <Panel {...mainPanelProps}>
            <BrowserbaseRenderer
              key={pendingState?.session_id}
              state={pendingState}
              verification={verification}
            />
          </Panel>
        </PanelGroup>
      </div>
    );
  }

  if (isAutomationReady || lastBrowserAgentInitiatedLog) {
    return (
      <div className="flex min-h-full">
        <PanelGroup className="" direction="horizontal">
          <Panel {...siderPanelProps}>
            <ApplicationHubSider
              application={application}
              browserTasks={browserRuns}
              key={currentBrowserTask?.browser_run_id || 'none'}
            />
          </Panel>

          <ResizableHandle withHandle />

          {/*<TaskBetaRunningView*/}
          {/*  browserTasks={browserTasks}*/}
          {/*  currentBrowserTask={currentBrowserTask}*/}
          {/*  qnaQuestions={qnaQuestions}*/}
          {/*  setIsQuestionSubmitted={setIsQuestionSubmitted}*/}
          {/*  setQnaQuestions={setQnaQuestions}*/}
          {/*  taskData={taskData}*/}
          {/*/>*/}
          <Panel {...mainPanelProps}>
            <>
              <BetaViewSwitch />
              <TaskRunningView application={application} />
            </>
          </Panel>
        </PanelGroup>
      </div>
    );
  }

  return (
    <div className="flex min-h-full">
      <PanelGroup className="" direction="horizontal">
        <Panel {...siderPanelProps}>
          <ApplicationHubSider
            application={application}
            browserTasks={browserRuns}
          />
        </Panel>

        <ResizableHandle withHandle />

        <Panel {...mainPanelProps}>
          <div className="flex flex-col gap-4 p-4">
            <div className="flex h-full items-center justify-center">
              <LoadingScreen />
            </div>
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
};

export default ApplicationHub;
